import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export const useUnreadMessages = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    const fetchUnreadCount = async () => {
      // Count unread received questions
      const { count: questionsCount } = await supabase
        .from("challenge_questions")
        .select("*", { count: "exact", head: true })
        .eq("recipient_id", user.id)
        .eq("is_read", false);

      // Count unread replies to user's questions
      const { count: repliesCount } = await supabase
        .from("challenge_question_replies")
        .select("*", { count: "exact", head: true })
        .eq("recipient_id", user.id)
        .eq("is_read", false);

      setUnreadCount((questionsCount || 0) + (repliesCount || 0));
    };

    fetchUnreadCount();

    // Real-time subscription for questions
    const questionsChannel = supabase
      .channel("unread-questions")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "challenge_questions",
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          setUnreadCount((prev) => prev + 1);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "challenge_questions",
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          // If message was marked as read, decrease count
          if (payload.old.is_read === false && payload.new.is_read === true) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "challenge_questions",
        },
        () => {
          // Refetch to get accurate count after delete
          fetchUnreadCount();
        }
      )
      .subscribe();

    // Real-time subscription for replies
    const repliesChannel = supabase
      .channel("unread-replies")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "challenge_question_replies",
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          setUnreadCount((prev) => prev + 1);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "challenge_question_replies",
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.old.is_read === false && payload.new.is_read === true) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(questionsChannel);
      supabase.removeChannel(repliesChannel);
    };
  }, [user]);

  return unreadCount;
};
